import React, { FunctionComponent, useState } from 'react';
import { FormikErrors, useFormik } from 'formik';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useSwipeable } from 'react-swipeable';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import { contactInfoValidationSchema } from '../../../../common/validation/yup';
import Input from '../../../../components/Input';
import RegularButton from '../../../../components/RegularButton';
import { IReferenceCandidateForm } from '../../../../common/models/reference/IReferenceCandidateForm';
import { IAppState } from '../../../../common/models/store/IAppState';
import { IContactMainInfo } from '../../../../common/models/contact/IContactMainInfo';
import { errorMessages, referenceExists, addThemselves } from '../../containers/EditReferenceForm/utils';
import { ErrorMessage } from '../../../../components/ErrorMessage';
import IconButton from '../../../../components/IconButton';
import Icon from '../../../../components/Icon';
import LoaderWrapper from '../../../../components/LoaderWrapper';

type ContactsInfo = {
  id?: string;
  email: string | null;
  phoneNumber: string | null;
}

interface IReferenceContactInfoFormProps {
  candidate: IContactMainInfo;
  currentReference: IReferenceCandidateForm;
  references: IReferenceCandidateForm[];
  value: ContactsInfo;
  invalidPhoneNumber?: string;
  isCheckingPhoneNumber: boolean;
  onGoBack: IBindingAction;
  onSubmit: IBindingCallback1<ContactsInfo>;
}

const ReferenceContactInfoForm: FunctionComponent<IReferenceContactInfoFormProps> = ({
  isCheckingPhoneNumber,
  candidate,
  currentReference: { firstName, lastName },
  references,
  invalidPhoneNumber,
  value,
  onGoBack,
  onSubmit
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = {
    id: value.id,
    email: value.email || '',
    phoneNumber: value.phoneNumber || ''
  };

  const getErrors = (errors: FormikErrors<ContactsInfo>, values: ContactsInfo) => (
    <div className="mt-3">
      {(values.email && errors.email) && <ErrorMessage {...errorMessages.email} />}
      {(values.phoneNumber && errors.phoneNumber) && <ErrorMessage {...errorMessages.phone} />}
      {referenceExists({ ...values, firstName, lastName } as IContactMainInfo, references)
        && <ErrorMessage {...errorMessages.existed1} />}
      {addThemselves({ ...values, firstName, lastName } as IContactMainInfo, candidate)
      && <ErrorMessage {...errorMessages.existed2} />}
      {Boolean(values.phoneNumber) && invalidPhoneNumber === values.phoneNumber
      && <ErrorMessage {...errorMessages.unreachablePhone} />}
    </div>
  );

  const isValid = (errors: FormikErrors<ContactsInfo>, values: ContactsInfo) => (
    ((!errors.phoneNumber && Boolean(values.phoneNumber) && !errors.email)
    || (!errors.email && Boolean(values.email) && !errors.phoneNumber))
    && !referenceExists({ ...values, firstName, lastName } as IContactMainInfo, references)
    && !addThemselves({ ...values, firstName, lastName } as IContactMainInfo, candidate)
    && (!values.phoneNumber || values.phoneNumber !== invalidPhoneNumber)
  );

  const onSave = ({ email, phoneNumber }: ContactsInfo) => {
    setIsSubmitting(true);
    if (isValid({}, { email, phoneNumber })) {
      onSubmit({
        email: email || null,
        phoneNumber: phoneNumber || null
      });
    }
  };

  const { handleSubmit, setFieldValue, values, errors } = useFormik({
    initialValues,
    onSubmit: onSave,
    validationSchema: contactInfoValidationSchema
  });
  const valid = isValid(errors, values);

  const swipeHandler = useSwipeable({
    onSwipedLeft: () => handleSubmit(),
    onSwipedRight: onGoBack,
    trackMouse: false
  });

  const isPhoneNumberValid = !errors.phoneNumber && values.phoneNumber !== invalidPhoneNumber;

  return (
    <div {...swipeHandler} className="cf-container position-relative">
      <LoaderWrapper transparent loading={isCheckingPhoneNumber}>
        <div className="cf-header-title mb-2">
          <FormattedMessage id="reference" defaultMessage="Reference" />
          &nbsp;
          {initialValues.id}
        </div>
        <div className="cf-title mb-2">
          <FormattedMessage
            id="candidateForm.contact.title"
            defaultMessage="How should we contact {firstName}?"
            values={{ firstName }}
          />
        </div>
        <div className="cf-subtitle cf-subtitle-width">
          <FormattedMessage
            id="candidateForm.contact.subtitle"
            defaultMessage="Provide an email address or mobile phone number that can receive text messages."
          />
        </div>
        <form onSubmit={handleSubmit} autoComplete="off" className="w-100">
          <div className="cf-form">
            <Input
              autoFocus
              type="text"
              label={{ id: 'email', defaultMessage: 'Email' }}
              value={values.email}
              error={isSubmitting && Boolean(errors.email && values.email)}
              success={!errors.email && Boolean(values.email)}
              onChange={val => setFieldValue('email', val)}
              variant="underlined"
              size="lg"
            />
            <Input
              type="phoneNumber"
              label={{ id: 'phoneNumber', defaultMessage: 'Phone number' }}
              value={values.phoneNumber}
              error={isSubmitting && Boolean(values.phoneNumber) && !isPhoneNumberValid}
              success={Boolean(values.phoneNumber) && isPhoneNumberValid}
              onChange={val => setFieldValue('phoneNumber', val)}
              variant="underlined"
              size="lg"
            />
          </div>
          <div className="cf-footer-btns">
            <IconButton
              size="lg"
              icon={<Icon icon="arrowLeft" />}
              onClick={onGoBack}
            />
            <RegularButton
              disabled={!valid}
              size="lg"
              textIntlId="candidateForm.next"
              defaultMessage="Next"
              icon={<Icon icon="arrowRight" />}
              iconPosition="right"
              onClick={() => {
                setIsSubmitting(true);
                if (!valid) {
                  handleSubmit();
                }
              }}
              enableAction
              type="submit"
            />
          </div>
          {isSubmitting && getErrors(errors, values)}
        </form>
      </LoaderWrapper>
    </div>
  );
};

const mapStateToProps = ({
  referenceRequest: {
    candidateId,
    firstName,
    lastName,
    phoneNumber,
    email,
    invalidPhoneNumber,
    isCheckingPhoneNumber
  }
}: IAppState) => ({
  candidate: { id: candidateId, firstName, lastName, phoneNumber, email },
  invalidPhoneNumber,
  isCheckingPhoneNumber
});

export default connect(mapStateToProps)(ReferenceContactInfoForm);
